import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { KIOSK_FULLSCREEN_OPT_OUT_ATTRIBUTE, KIOSK_FULLSCREEN_STORAGE_KEY } from '../../../constants/kiosk';
import { IsKioskBenchPath } from '../../../utils/kioskAccount';
import { useKiosk } from './KioskContext';

const FLAG_ON = '1';

// Teclas do NAVEGADOR: F11 alterna a tela cheia dele e Esc sai da nossa (nem
// conta como gesto). Brigar com elas só piscaria a tela; o próximo bipe ou
// clique devolve a tela cheia.
const BROWSER_KEYS = new Set(['F11', 'Escape']);

function readFlag() {
    try {
        return localStorage.getItem(KIOSK_FULLSCREEN_STORAGE_KEY);
    } catch {
        return null;
    }
}

/** Liga a flag na primeira abertura; um '0' posto pelo supervisor fica. */
function armFlag() {
    try {
        if (localStorage.getItem(KIOSK_FULLSCREEN_STORAGE_KEY) === null) {
            localStorage.setItem(KIOSK_FULLSCREEN_STORAGE_KEY, FLAG_ON);
        }
    } catch {
        // sem storage (aba anônima, bloqueio): a bancada segue sem a flag
    }
}

/**
 * TELA CHEIA automática da BANCADA de quiosque (/bancada dentro do KioskShell).
 *
 * O navegador só concede tela cheia dentro de um GESTO do usuário, então não
 * existe timer: a tela ouve (na captura, antes de qualquer página) cada
 * `pointerup` e `keydown` — o bipe do leitor é keydown — e, fora da tela
 * cheia, pede de novo. É `pointerup`, não `pointerdown`: a tela cresce ao
 * entrar em tela cheia e um modal centralizado andaria entre o apertar e o
 * soltar, perdendo o clique; no soltar o clique sai no mesmo instante, antes
 * de a tela crescer (o gesto do `pointerdown` continua valendo por alguns
 * segundos, então o pedido é aceito). F11/Esc continuam
 * funcionando; a interação seguinte volta. Elemento marcado com
 * `data-sem-tela-cheia` (o botão Melidash, que SAI da tela cheia) é ignorado.
 *
 * Só vale com a flag ligada (constants/kiosk: KIOSK_FULLSCREEN_STORAGE_KEY) e
 * só em /bancada — em /warehouse/bench (admin, com sidebar) não faz nada.
 */
export function useKioskFullscreen() {
    const kiosk = useKiosk();
    const { pathname } = useLocation();
    const active = kiosk && IsKioskBenchPath(pathname);

    useEffect(() => {
        if (!active) return undefined;
        armFlag();

        // Vários keydowns do leitor chegam antes de o primeiro pedido resolver:
        // um pedido por vez.
        let pending = false;
        const enter = () => {
            if (pending || document.fullscreenElement || readFlag() !== FLAG_ON) return;
            const root = document.documentElement;
            if (typeof root.requestFullscreen !== 'function') return;
            pending = true;
            try {
                Promise.resolve(root.requestFullscreen())
                    .catch(() => {
                        // negado (sem gesto, iframe, política): a próxima interação tenta
                    })
                    .finally(() => {
                        pending = false;
                    });
            } catch {
                pending = false;
            }
        };

        const onInteraction = (event) => {
            if (event.type === 'keydown' && BROWSER_KEYS.has(event.key)) return;
            const target = event.target;
            if (typeof target?.closest === 'function' && target.closest(`[${KIOSK_FULLSCREEN_OPT_OUT_ATTRIBUTE}]`)) {
                return;
            }
            enter();
        };

        // Ao abrir: logo depois do login o clique em "Entrar" ainda vale como
        // gesto e a tela cheia entra na hora. Depois de um F5 não vale — aí nem
        // tenta (evita o aviso do navegador) e o primeiro bipe/clique resolve.
        if (!navigator.userActivation || navigator.userActivation.isActive) {
            enter();
        }

        document.addEventListener('pointerup', onInteraction, true);
        document.addEventListener('keydown', onInteraction, true);
        return () => {
            document.removeEventListener('pointerup', onInteraction, true);
            document.removeEventListener('keydown', onInteraction, true);
        };
    }, [active]);
}
